import 'numeral/locales/no';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import { CreditCardForm } from '@eika-felles/kyc-declaration-lib';

import {
    changeSoknadField,
    FormTypeFeedback,
    sendFeedback,
    summaryNextEvent,
    updateConsent,
    verifyBestillingSkjema,
    verifySoknadField,
} from '../../../actions';
import { DispatchWithState, NameValueStringEvent, StringKeyValue } from '../../../actions/types';
import { AppState } from '../../../reducers';
import { ApplicationReducer } from '../../../reducers/application';
import { CardsReducer } from '../../../reducers/cards';
import { ConsentReducer } from '../../../reducers/consent';
import { ContextReducer } from '../../../reducers/context';
import { KdkReducer } from '../../../reducers/kdk';
import { LoaderReducer } from '../../../reducers/loader';
import { UserReducer } from '../../../reducers/user';
import getReturnLinkKey from '../../../util/navUtil';
import { trackEvent, TrackingStep } from '../../../util/piwik-utils';
import InfoBox from '../../Messages/InfoBox';
import Loading from '../../Messages/Loading';
import StepButtons from '../../Steps/StepButtons';
import SummaryComponent from './SummaryComponent';
import SummaryControl from './SummaryControl';
import SummaryDeclined from './SummaryDeclined';

interface Props {
    action: {
        sendFeedback: (form: FormTypeFeedback) => DispatchWithState;
        changeSoknadField: NameValueStringEvent;
        verifySoknadField: NameValueStringEvent;
        verifyBestillingSkjema: any;
        summaryNextEvent: any;
        updateConsent: any;
    };
    application: ApplicationReducer;
    context: ContextReducer;
    consent: ConsentReducer;
    loader: LoaderReducer;
    text: StringKeyValue;
    cards: CardsReducer;
    user: UserReducer;
    kdk: KdkReducer;
}

const Summary = (props: Props) => {
    const [step, setStep] = useState(1);

    const { loader, cards, text, application, consent, user, context, action, kdk } = props;
    const { isSendingOrder } = loader;
    const { kortId } = cards;
    const { kredittgrense } = application;
    const { showKYC } = kdk;

    if (isSendingOrder) {
        return <Loading title={kredittgrense ? text.loadingDefault : text.loadingCreditCheck} />;
    }

    const returnLink = text[getReturnLinkKey(user, context)];

    if (application.status !== 'INNVILGET') {
        if (application.status === 'AVSLAATT') {
            return (
                <div className="summary">
                    <SummaryDeclined application={application} text={text} returnLink={returnLink} />
                </div>
            );
        }
        if (application.status !== 'TIL_SIGNERING') {
            return (
                <div className="summary">
                    <SummaryControl action={action} loader={loader} text={text} returnLink={returnLink} />
                </div>
            );
        }
    }

    return (
        <div className="summary">
            {showKYC ? (
                <>
                    {step === 1 && (
                        <>
                            <SummaryComponent
                                application={application}
                                consent={consent}
                                text={text}
                                cards={cards}
                                action={action}
                                showSubmitButton={false}
                            />
                            <StepButtons
                                showNext
                                nextChangeEvent={() => {
                                    trackEvent(TrackingStep.SummaryKyc);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                    setStep(2);
                                }}
                            />
                        </>
                    )}
                    {step === 2 && (
                        <>
                            {/* @ts-ignore */}
                            <CreditCardForm
                                publicId={user.ssn}
                                statusCallback={() => {
                                    action.summaryNextEvent();
                                }}
                                goBackText={text.applicationBack}
                                goBackCallback={() => setStep(1)}
                            />
                        </>
                    )}
                </>
            ) : (
                <>
                    <SummaryComponent
                        application={application}
                        consent={consent}
                        text={text}
                        cards={cards}
                        action={action}
                        showSubmitButton
                    />
                </>
            )}

            <div style={{ marginTop: '40px' }}>
                <InfoBox type="info">{kortId === '' ? text.defaultInfoBoxText : text.summaryInfoBoxApproved}</InfoBox>
            </div>
        </div>
    );
};

const mapStateToProps = (state: AppState) => ({
    text: state.context.config.data.text,
    application: state.application,
    consent: state.consent,
    loader: state.loader,
    context: state.context,
    cards: state.cards,
    user: state.user,
    kdk: state.kdk,
    feature: state.feature,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    action: bindActionCreators(
        {
            verifyBestillingSkjema,
            verifySoknadField,
            changeSoknadField,
            summaryNextEvent,
            updateConsent,
            sendFeedback,
        },
        dispatch,
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Summary);
